import { LockIcon } from '@approvalmax/ui';
import { Grid } from '@approvalmax/ui/src/components';
import { errorHelpers, eventHelpers } from '@approvalmax/utils';
import { StatusBadge, StatusBadgeSize, useResolutionBadge } from 'modules/components';
import { domain } from 'modules/data';
import { FC, ReactNode, useEffect, useRef } from 'react';
import bemFactory from 'react-bem-factory';
import { getPath, Path } from 'urlBuilder';

import { useRequestDeadline } from '../../hooks/useRequestDeadline';
import { useAmount, useScheduledPaymentLabel, useWatchers } from './RequestListItem.hooks';
import { messages } from './RequestListItem.messages';
import {
    AmCircleLogoStyled,
    Amount,
    AmountRow,
    ApprovalMaxAvatar,
    BadgeEyeIcon,
    BadgeWrapper,
    Body,
    CompanyName,
    CompanyRow,
    Content,
    DeadlineWarn,
    DearAvatar,
    DraftBadge,
    LeftSide,
    NarrationLine,
    NetSuiteAvatar,
    QBooksAvatar,
    ReceiptBankAvatar,
    Root,
    ScheduledPayment,
    Title,
    TitleRow,
    XeroAvatar,
} from './RequestListItem.styles';
import { RequestListItemProps } from './RequestListItem.types';

const qa = bemFactory.qa('reql-request-list-item');

export const RequestListItem: FC<RequestListItemProps> = (props) => {
    const { request, selected, onClick } = props;

    const amount = useAmount(request);
    const watchers = useWatchers(request);
    const resolutionBadge = useResolutionBadge(request);
    const deadline = useRequestDeadline(request);
    const scheduledPaymentLabel = useScheduledPaymentLabel(request);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (selected) {
            // explicitly focus the item, plus tabIndexes in the component are cover bug
            // within react-virtuoso (4.12.0) when focus might be lost while navigating
            // through list via keyboard
            rootRef.current?.focus();
        }
    }, [selected]);

    let avatarContent: ReactNode;

    switch (request.origin) {
        case domain.RequestOrigin.ReceiptBank:
            avatarContent = <ReceiptBankAvatar width={40} height={40} />;
            break;

        case domain.RequestOrigin.ApprovalMax:
        case domain.RequestOrigin.PublicApi:
            avatarContent = <ApprovalMaxAvatar user={request.author} size={40} />;
            break;

        case domain.RequestOrigin.Email:
            avatarContent = <AmCircleLogoStyled size={40} />;
            break;

        case domain.RequestOrigin.Xero:
            avatarContent = <XeroAvatar width={40} height={40} />;
            break;

        case domain.RequestOrigin.NetSuite:
            avatarContent = <NetSuiteAvatar width={40} height={40} />;
            break;

        case domain.RequestOrigin.QBooks:
            avatarContent = <QBooksAvatar width={40} height={40} />;
            break;

        case domain.RequestOrigin.Dear:
            avatarContent = <DearAvatar width={40} height={40} />;
            break;

        default:
            throw errorHelpers.assertNever(request.origin);
    }

    return (
        <Root
            $active={selected}
            data-qa={qa()}
            data-qa-id={request.id}
            data-qa-name={request.displayName}
            role='listitem'
            tabIndex={0}
            ref={rootRef}
            onClick={onClick}
        >
            <Content
                tabIndex={-1}
                // we are handling a link click manually by Root. The anchor here needed to keep a
                // link to a request in case if a user want to open it in a new tab or copy
                onClick={eventHelpers.preventDefaultCallback}
                to={getPath(Path.request, request.id, request.companyId)}
            >
                {avatarContent}

                <Body>
                    <Grid gridTemplateColumns='auto max-content' columnGap={12} alignItems='flex-start'>
                        <LeftSide>
                            <TitleRow>
                                <Title data-qa={qa('title')} title={request.displayName}>
                                    {request.displayName}
                                </Title>
                            </TitleRow>

                            {request.integrationCode === domain.IntegrationCode.XeroManualJournal &&
                                request.details.narration && (
                                    <NarrationLine title={request.details.narration}>
                                        {request.details.narration}
                                    </NarrationLine>
                                )}
                        </LeftSide>

                        <BadgeWrapper>
                            {watchers.showIcon && <BadgeEyeIcon />}

                            {request.statusV2 === domain.RequestStatusV2.OnHold && <LockIcon size={16} />}

                            <div>
                                {request.statusV2 === domain.RequestStatusV2.Draft && (
                                    <DraftBadge data-qa={qa('draft-badge')}>{messages.draftBadgeText}</DraftBadge>
                                )}

                                {resolutionBadge && (
                                    <StatusBadge
                                        mode={resolutionBadge.mode}
                                        label={resolutionBadge.label}
                                        hasIcon={resolutionBadge.hasIcon}
                                        tooltip={resolutionBadge.tooltip}
                                        size={StatusBadgeSize.Small}
                                        qa={qa('resolution-badge', resolutionBadge.qaMode)}
                                    />
                                )}
                            </div>
                        </BadgeWrapper>
                    </Grid>

                    {amount && (
                        <AmountRow>
                            <Amount data-qa={qa('amount')} title={amount.title}>
                                {amount.text}
                            </Amount>
                        </AmountRow>
                    )}

                    <CompanyRow>
                        <CompanyName data-qa={qa('company-name')} title={request.company.displayName}>
                            {request.company.displayName}
                        </CompanyName>

                        {deadline && (
                            <DeadlineWarn
                                data-qa={qa('deadline-warn')}
                                title={deadline.title}
                                $warning={deadline.warning}
                            >
                                {deadline.text}
                            </DeadlineWarn>
                        )}

                        {scheduledPaymentLabel && (
                            <ScheduledPayment data-qa={qa('scheduled-payment')} $warning={scheduledPaymentLabel.failed}>
                                Scheduled for {scheduledPaymentLabel.dateString}
                            </ScheduledPayment>
                        )}
                    </CompanyRow>
                </Body>
            </Content>
        </Root>
    );
};

RequestListItem.displayName = 'RequestListItem';
